import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/**
 * SEO outputs resolved by category-api `resolve-browse-url`. category-api is the
 * source of truth for the canonical URL, indexability and H1 label; the page
 * obeys these rather than re-deriving them from the pathname.
 */
export type BrowseSeoMeta = {
    /** Canonical browse path (relative, leading slash) the page should self-identify as. */
    canonicalPath: string | null;
    /** Robots meta content, e.g. `index, follow` / `noindex, follow`. */
    robots: string | null;
    /** Whether the resolved page is indexable. `null` when the API did not resolve. */
    seoIndexable: boolean | null;
    /** Authoritative category display label for the H1. */
    label: string | null;
};

export type BrowsePathState = {
    baseCategoryIds: number[];
    /** SEO browse slug last associated with `baseCategoryIds` (resolver fallback / same-slug reuse). */
    lastBrowseCategorySlug: string | null;
    /** Full pathname last associated with `baseCategoryIds` / pre-search facet mapping. */
    lastBrowsePathname: string | null;
    seo: BrowseSeoMeta;
};

const emptySeoMeta: BrowseSeoMeta = {
    canonicalPath: null,
    robots: null,
    seoIndexable: null,
    label: null,
};

const initialState: BrowsePathState = {
    baseCategoryIds: [],
    lastBrowseCategorySlug: null,
    lastBrowsePathname: null,
    seo: { ...emptySeoMeta },
};

export type SetBrowseBaseCategoryIdsPayload = {
    ids: number[];
    /** Pass the current browse slug on `/c/:browseCategorySlug/*` routes; `null` when not on that route. */
    browseCategorySlug: string | null;
    /** Full pathname for the resolved browse route; gates reuse of prior search facets. */
    browsePathname?: string | null;
};

export const browsePathSlice = createSlice({
    initialState,
    name: 'browsePath',
    reducers: {
        clearBrowseBaseCategoryIds(state) {
            state.baseCategoryIds = [];
            state.lastBrowseCategorySlug = null;
            state.lastBrowsePathname = null;
            state.seo = { ...emptySeoMeta };
        },
        clearBrowseSeoMeta(state) {
            state.seo = { ...emptySeoMeta };
        },
        setBrowseSeoMeta(state, action: PayloadAction<BrowseSeoMeta>) {
            state.seo = action.payload;
        },
        setBrowseBaseCategoryIds(state, action: PayloadAction<SetBrowseBaseCategoryIdsPayload>) {
            const { ids, browseCategorySlug, browsePathname } = action.payload;
            state.baseCategoryIds = ids.filter((id) => !Number.isNaN(id) && id > 0);
            if (!browseCategorySlug) {
                state.lastBrowseCategorySlug = null;
                state.lastBrowsePathname = null;
                return;
            }
            if (state.baseCategoryIds.length > 0) {
                state.lastBrowseCategorySlug = browseCategorySlug;
                state.lastBrowsePathname = browsePathname ?? null;
            } else {
                state.lastBrowseCategorySlug = null;
                state.lastBrowsePathname = null;
            }
        },
    },
});

export const { clearBrowseBaseCategoryIds, clearBrowseSeoMeta, setBrowseBaseCategoryIds, setBrowseSeoMeta } =
    browsePathSlice.actions;
export const browsePathReducer = browsePathSlice.reducer;
